/**
 * Service Transparency Ledger Modal
 * Everlane-style cost breakdown for HydraFacial and other services
 * 
 * LOCKED LAYOUT:
 * - Header: Espresso gradient with gold "WHERE IT GOES" eyebrow
 * - Ledger rows: off-white body, uppercase letterspaced labels, sand hairlines
 * - YOUR HOURLY and YOUR COMMISSION rows in bronze with espresso rules
 * - Footer: "Both lines are yours — plus your tip on top."
 * 
 * Rules:
 * - Commission line personalizes per viewer
 * - Kim/custom pay and trainees without commission see hourly only
 * - Consumable amounts display with ~ to signal honest approximation
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import api from '@/lib/api';

const ServiceTransparencyModal = ({ isOpen, onClose, serviceName }) => {
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConsumablesDetail, setShowConsumablesDetail] = useState(false);
  const [showOverheadDetail, setShowOverheadDetail] = useState(false);

  const fetchBreakdown = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const breakdownRes = await api.get(`/services/transparency/${encodeURIComponent(serviceName)}`);
      setBreakdown(breakdownRes.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [serviceName]);

  useEffect(() => {
    if (isOpen && serviceName) {
      fetchBreakdown();
    }
  }, [isOpen, serviceName, fetchBreakdown]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header - Espresso gradient block */}
        <div className="bg-gradient-to-br from-[#3d2817] to-[#2a1810] px-6 py-8 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Gold uppercase eyebrow */}
          <div className="text-[#d4a574] text-xs font-bold tracking-[0.15em] uppercase mb-3">
            WHERE IT GOES
          </div>
          
          {/* Service name in Cormorant ivory */}
          <h2 className="font-['Cormorant_Garamond'] text-[26px] font-semibold text-[#f5f5f0] leading-tight mb-2">
            {serviceName}
          </h2>
          
          {/* Price and duration in Cormorant gold */}
          {breakdown && (
            <>
              <div className="font-['Cormorant_Garamond'] text-[20px] text-[#d4a574]">
                ${breakdown.price.toFixed(2)} · {breakdown.duration_minutes} min
              </div>
              {/* Promo note if applicable */}
              {breakdown.is_promo && breakdown.promo_note && (
                <div className="text-xs text-[#d4a574] mt-1 italic">
                  {breakdown.promo_note}
                </div>
              )}
            </>
          )}
        </div>

        {/* Ledger rows - off-white body */}
        <div className="bg-[#faf9f6] px-6">
          {loading && (
            <div className="py-12 text-center text-[#5a4a3a]">
              Loading breakdown...
            </div>
          )}

          {error && (
            <div className="py-12 text-center text-red-700">
              {error}
            </div>
          )}

          {breakdown && !loading && (
            <>
              {/* Consumables row - EXPANDABLE */}
              {breakdown.has_cost_data && breakdown.breakdown.consumables_total > 0 && (
                <div className="border-b border-[#e8e4dc]">
                  <button
                    onClick={() => setShowConsumablesDetail(!showConsumablesDetail)}
                    className="w-full flex justify-between items-center py-[17px] hover:bg-[#f5f1e8] transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold tracking-[0.1em] uppercase text-[#3d2817]">
                        CONSUMABLES
                      </span>
                      {showConsumablesDetail ? (
                        <ChevronUp className="w-4 h-4 text-[#3d2817]" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-[#3d2817]" />
                      )}
                    </div>
                    <span className="text-[15px] text-[#2a1810]">
                      ~${breakdown.breakdown.consumables_total.toFixed(2)}
                    </span>
                  </button>
                  
                  {/* Itemized detail (expandable) */}
                  {showConsumablesDetail && (
                    <div className="pb-4 px-4 bg-[#f5f1e8]">
                      <div className="space-y-2 mb-3">
                        {breakdown.breakdown.consumables_itemized.map((item) => (
                          <div key={item.name} className="flex justify-between text-sm">
                            <span className="text-[#5a4a3a]">{item.name}</span>
                            <span className="text-[#3d2817]">~${item.cost.toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      {breakdown.breakdown.consumables_notes && (
                        <div className="text-xs text-[#5a4a3a] italic border-t border-[#e8e4dc] pt-2">
                          {breakdown.breakdown.consumables_notes}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Equipment row - NO STEAM */}
              <div className="flex justify-between items-center py-[17px] border-b border-[#e8e4dc]">
                <span className="text-xs font-bold tracking-[0.1em] uppercase text-[#3d2817]">
                  EQUIPMENT — {breakdown.breakdown.equipment_label}
                </span>
                <span className="text-[15px] text-[#2a1810]">
                  ${breakdown.breakdown.equipment_total.toFixed(2)}
                </span>
              </div>

              {/* TOTAL SERVICE EXPENSE - Bold subtotal with heavier top rule */}
              <div className="flex justify-between items-center py-[17px] border-t-2 border-b border-[#2a1810]">
                <span className="text-xs font-bold tracking-[0.1em] uppercase text-[#3d2817]">
                  TOTAL SERVICE EXPENSE
                </span>
                <span className="text-[15px] font-bold text-[#2a1810]">
                  ${breakdown.breakdown.total_service_expense.toFixed(2)}
                </span>
              </div>

              {/* STUDIO OVERHEAD - EXPANDABLE */}
              <div className="border-b border-[#e8e4dc]">
                <button
                  onClick={() => setShowOverheadDetail(!showOverheadDetail)}
                  className="w-full flex justify-between items-center py-[17px] hover:bg-[#f5f1e8] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold tracking-[0.1em] uppercase text-[#3d2817]">
                      STUDIO OVERHEAD
                    </span>
                    {showOverheadDetail ? (
                      <ChevronUp className="w-4 h-4 text-[#3d2817]" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-[#3d2817]" />
                    )}
                  </div>
                  <span className="text-[15px] text-[#2a1810]">
                    ${breakdown.breakdown.studio_overhead_total.toFixed(2)}
                  </span>
                </button>
                
                {/* Itemized overhead detail (expandable) */}
                {showOverheadDetail && (
                  <div className="pb-4 px-4 bg-[#f5f1e8]">
                    <div className="space-y-2 mb-3">
                      {breakdown.breakdown.studio_overhead_itemized.map((item) => (
                        <div key={item.name} className="flex justify-between text-sm">
                          <span className="text-[#5a4a3a]">{item.name}</span>
                          <span className="text-[#3d2817]">${item.cost.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="text-xs text-[#5a4a3a] italic border-t border-[#e8e4dc] pt-2">
                      The studio's fixed costs, shared across every treatment on the books.
                    </div>
                  </div>
                )}
              </div>

              {/* Credit Card Fees row */}
              <div className="flex justify-between items-center py-[17px] border-b border-[#e8e4dc]">
                <span className="text-xs font-bold tracking-[0.1em] uppercase text-[#3d2817]">
                  CREDIT CARD FEES ({breakdown.breakdown.card_fee_percent}%)
                </span>
                <span className="text-[15px] text-[#2a1810]">
                  ${breakdown.breakdown.card_fees.toFixed(2)}
                </span>
              </div>

              {/* GT remainder row - server suppresses (null) unless admin or toggle on (Item 23) */}
              {breakdown.has_cost_data && breakdown.breakdown.gt_remainder != null && (
                <div className="flex justify-between items-center py-[17px] border-b border-[#e8e4dc]">
                  <span className="text-xs font-bold tracking-[0.1em] uppercase text-[#3d2817]">
                    GT — KEEPS IT RUNNING
                  </span>
                  <span className="text-[15px] text-[#2a1810]">
                    ${breakdown.breakdown.gt_remainder.toFixed(2)}
                  </span>
                </div>
              )}

              {breakdown.breakdown.employee_total != null && (<>
              {/* YOUR TOTAL EARNINGS - Final combined row with bronze, double rules */}
              <div className="py-6 my-4 border-t-2 border-b-2 border-[#2a1810]">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-sm font-medium tracking-wide text-[#b8860b] block mb-1">
                      YOUR TOTAL EARNINGS
                    </span>
                    {breakdown.show_commission ? (
                      <div className="text-xs text-[#5a4a3a]">
                        HOURLY ${breakdown.breakdown.employee_hourly.toFixed(2)} · {breakdown.duration_minutes} MIN + COMMISSION ${breakdown.breakdown.employee_commission.toFixed(2)} AT {breakdown.breakdown.employee_commission_rate}%
                        {breakdown.is_mid_period && (
                          <span className="italic ml-1">· estimated</span>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-[#5a4a3a]">
                        HOURLY ${breakdown.breakdown.employee_hourly.toFixed(2)} · {breakdown.duration_minutes} MIN
                      </div>
                    )}
                  </div>
                  <span className="font-['Cormorant_Garamond'] text-[26px] font-semibold text-[#b8860b]">
                    ${breakdown.breakdown.employee_total.toFixed(2)}
                  </span>
                </div>
              </div>
              </>)}

              {/* Footer message */}
              <div className="pt-4 pb-6">
                <p className="font-['Cormorant_Garamond'] italic text-[14.5px] text-[#5a4a3a] text-center leading-relaxed">
                  {breakdown.footer_message}
                </p>
                {!breakdown.has_cost_data && (
                  <p className="text-xs text-center text-gray-500 mt-2">
                    {breakdown.notes}
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceTransparencyModal;
